import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Theme } from "@emotion/react";
import { zIndexes, borderRadius, styled } from "@iventis/styles";

interface StyledSidebarNodeProps {
    selected: boolean;
    showRighthandButtons?: boolean;
    expanded: boolean;
    hidden: boolean;
    locked: boolean;
    type: NodeType;
    nodeHeight: number;
    indentLevel: number;
    isDragging?: boolean;
}

export const StyledSidebarNode = styled.div<StyledSidebarNodeProps>`
    display: flex;
    flex-direction: row;
    justify-content: stretch;
    position: relative;
    width: 100%;
    padding: 0px;
    height: ${({ nodeHeight }) => `${nodeHeight}px`};
    cursor: pointer;
    color: ${({ theme }) => theme.shades.one};
    opacity: ${({ isDragging }) => (isDragging ? "50%" : "100%")};
    z-index: ${zIndexes.mapSidebar.node};
    .node-segment {
        position: relative;
        z-index: ${zIndexes.mapSidebar.nameSegment};
        display: flex;
        align-items: center;
        min-width: 0;
        flex: 1 1;
    }
    .icon {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        justify-content: center;
    }
    .link-icon {
        display: flex;
        align-items: center;
        position: absolute;
        height: 35%;
        width: 35%;
        right: 4px;
        top: 4px;
        z-index: 200;
        border-radius: ${borderRadius.circle};
        transform: scale(0.8);
        color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
        background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    }
    .node-options {
        position: relative;
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        height: 100%;
        display: flex;
        align-items: center;
    }
    .caret,
    .layer-overflow,
    .lock,
    .eye {
        width: ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        flex: 0 0 ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        &:not(.disabled) {
            :hover {
                color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
            }
        }
        color: ${({ hidden, theme }: { theme: Theme; hidden: boolean }) => (hidden ? theme.typographyColors.moreSubdued : "initial")};
    }
    .caret {
        display: inline-block;
    }
    .layer-overflow,
    .lock,
    .eye {
        ${({ hidden, selected, locked }) => (!hidden && !selected && !locked ? "display: none;" : "")};
    }
    .lock > *,
    .eye > *,
    .layer-overflow > * {
        display: ${({ selected }) => (selected ? "inline-block" : "none")};
    }
    .eye > * {
        ${({ hidden }) => (hidden ? "display: inline-block" : "")}
    }
    .lock > * {
        ${({ locked }) => (locked ? "display: inline-block" : "")}
    }
    .disabled > svg {
        color: ${({ theme }: { theme: Theme }) => theme.shades.three};
    }
    .preview-icon,
    .indent {
        flex: 0 0
            ${({ theme, indentLevel, expanded }: { theme: Theme } & StyledSidebarNodeProps) =>
                expanded ? `${20 * indentLevel}px` : `calc(${theme.sizes.iconButtonWidth} + ${20 * indentLevel}px)`};
    }
    .preview-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 ${({ theme }: { theme: Theme }) => theme.sizes.iconButtonWidth};
        .preview-background {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 20px;
            height: 20px;
            border-radius: ${borderRadius.circle};
            background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
        }

        .style-preview-icon {
            width: 20px;
            height: 20px;
        }

        :hover {
            .preview-background {
                background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
            }
        }
    }
    .name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-style: ${({ type }) => (type === NodeType.MapObject ? "italic" : "normal")};
    }
    .hover-indicator {
        position: absolute;
        z-index: 0;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
        background-color: ${({ theme, selected }: { theme: Theme; selected: boolean }) => (selected ? theme.primaryColors.blue50 : "none")};
    }
    &.sidebar-node-MapObject {
        .hover-indicator {
            background-color: ${({ theme, selected }: { theme: Theme; selected: boolean }) => (selected ? theme.primaryColors.blue25 : "none")};
        }
        :hover {
            .hover-indicator {
                background-color: ${({ theme, selected }: { theme: Theme; selected: boolean }) => (selected ? theme.primaryColors.blue25 : theme.secondaryColors.light20)};
            }
        }
    }
    :hover {
        .hover-indicator {
            background-color: ${({ theme, selected }: { theme: Theme; selected: boolean }) => (selected ? theme.primaryColors.blue50 : theme.secondaryColors.light20)};
        }
        .layer-overflow > *,
        .lock > *,
        .eye > *,
        .eye,
        .lock,
        .layer-overflow {
            display: inline-block;
        }

        .layer-overflow,
        .eye,
        .lock,
        .caret {
            color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
        }
    }
    .layer-overflow {
        height: 100%;
    }
`;

export const SelectedNodeIndicator = styled.div`
    width: 5px;
    background-color: rgba(129, 118, 227, 0.8);
    height: 44px;
    position: absolute;
    left: 2px;
    z-index: ${zIndexes.mapSidebar.selectedIndicator};
`;
